import React, { useState, useEffect } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import {
    Button,
    TextField,
    createMuiTheme,
    Dialog, DialogActions, DialogContent, DialogTitle,
    MuiThemeProvider, Typography
} from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import MultilanguageTabBlock from '../../../components/MultilanguageTabBlock'
import { useSkeleton } from '../../../hooks/useSkeleton'
import { alertError, alertSuccess } from '../../../../utils/logger'
import { getFeatureById, postFeature, updateFeature, deleteFeature } from '../../../../api/feature/index'
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { getAllActiveSubPlans } from '../../../../api/subPlan'
import FeatureTable from './FeatureTable'
import TablePlan from './TablePlan'
import { getNonEmpty } from '../../../../utils/helpers';

const theme = createMuiTheme({
    palette: {
        secondary: {
            main: "#F64E60",
        },
    },
});


function getEmptyText() {
    return {
        title: {
            'es': "",
            'en': ""
        }
    }
}

function getEmptyFeatures() {
    return {
        title: {
            'es': "",
            'en': ""
        },
        subPlan: [],
    }
}

function featureDialog(featuresOpen, setFeaturesOpen, featureSelected, setFeatureSelected, plans, selectedPlans, setSelectedPlans, featuresData, setFeatureData, newFeature, setTableKey) {

    const dialogTitle = newFeature ? "Create feature" : "Edit feature"


    const handleChange = (element, lang) => (event) => {
        if (lang) {
            if (event.target.value === ' ')
                return
            if (!featureSelected[element])
                featureSelected[element] = {}
            let newText = featureSelected[element]
            newText[lang] = event.target.value
            setFeatureSelected({ ...featureSelected, [element]: newText })
        } else
            setFeatureSelected({ ...featureSelected, [element]: event.target.value })
    }

    const renderMultilanguageTabContent = lang => {
        return (
            <>
                <TextField
                    label="Title"
                    value={(featureSelected.title && featureSelected.title[lang]) || ''}
                    onChange={handleChange('title', lang)}
                    margin="normal"
                    variant="outlined"
                    required
                />
                <FeatureTable
                    data={plans}
                    featureData={selectedPlans}
                    setFeatureData={setSelectedPlans}
                />
            </>
        )
    }

    return (
        <Dialog
            open={featuresOpen}
            onClose={() => setFeaturesOpen(false)}
            aria-labelledby="confirm-dialog">
            <DialogTitle id="confirm-dialog">{dialogTitle}</DialogTitle>
            <DialogContent style={{ overflowY: 'hidden' }}>
                <MultilanguageTabBlock
                    multilanguageTabContent={renderMultilanguageTabContent}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                        if (!featureSelected.title?.es || featureSelected.title?.es.trim() === '') {
                            alertError({ customMessage: "Spanish title is required." });
                            return;
                        }

                        if (featureSelected.title?.en === '' || featureSelected.title?.en === '') {
                            alertError({ customMessage: "English title is required." });
                            return;
                        }
                        featureSelected.subPlan = selectedPlans
                        if (dialogTitle.includes("Create")) {
                            setFeatureData([...featuresData, featureSelected]);
                        } else {
                            const updatedData = featuresData.map((item) =>
                                item.title.es === featureSelected.title.es ? featureSelected : item
                            );
                            setFeatureData([...updatedData]);
                        }

                        setTableKey(k => k + 1);
                        setFeatureSelected(getEmptyFeatures())
                        setSelectedPlans([])
                        setFeaturesOpen(false)
                    }}
                >
                    Save
                </Button>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                        setFeaturesOpen(false)
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}


export default function EditFeaturePage() {
    const featureId = useParams().id;
    const history = useHistory();
    const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } = useSkeleton();

    const [feature, setFeature] = useState(getEmptyText())
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const [featuresData, setFeatureData] = useState([])
    const [featuresOpen, setFeaturesOpen] = useState(false);
    const [featureSelected, setFeatureSelected] = useState(getEmptyFeatures([]))

    const [plans, setPlans] = useState([])
    const [selectedPlans, setSelectedPlans] = useState([])

    const [newFeature, setNewFeature] = useState(false)
    const [tableKey, setTableKey] = useState(0);


    useEffect(() => {
        getAllActiveSubPlans()
            .then(res => {
                const data = res.data.map(item => ({
                    ...item,
                    title: item.target.charAt(0).toUpperCase() + item.target.slice(1).toLowerCase() + " - " + getNonEmpty(item.title)
                }))
                setPlans(data)
            }
            )
            .catch(error => {
                alertError({ error: error, customMessage: 'Could not get subscription plans.' })
            })
        if (!featureId) {
            disableLoadingData();
            return
        }

        getFeatureById(featureId).then(res => {
            setFeature(res.data)
            setFeatureData(res.data.featurePlan)
            disableLoadingData()
        }).catch(error => {
            alertError({ error: error, customMessage: 'Could not get feature.' })
            history.push('/features')
        })
    }, [featureId, disableLoadingData, history])

    function saveFeature() {

        feature.featurePlan = featuresData
        if (!featureId) {
            postFeature(feature)
                .then((res) => {
                    alertSuccess({
                        title: "Saved!",
                        customMessage: "Feature created successfully.",
                    });
                    history.push("/features");
                })
                .catch((error) => {
                    alertError({
                        error: error,
                        customMessage: "Could not save the feature.",
                    });
                });
        } else {
            updateFeature(featureId, feature).then(res => {
                alertSuccess({ title: 'Saved!', customMessage: 'Changes successfully saved.' })
                history.push('/features')
            }).catch(error => {
                alertError({ error: error, customMessage: 'Could not save changes.' })
            })
        }
    }

    const handleChange = (element, lang) => (event) => {
        if (lang) {
            if (event.target.value === ' ')
                return
            if (!feature[element])
                feature[element] = {}
            let newText = feature[element]
            newText[lang] = event.target.value
            setFeature({ ...feature, [element]: newText })
        } else
            setFeature({ ...feature, [element]: event.target.value })
    }


    const renderMultilanguageTabContent = lang => {
        return (
            <>
                <TextField
                    label="Title"
                    value={(feature.title && feature.title[lang]) || ''}
                    onChange={handleChange('title', lang)}
                    margin="normal"
                    variant="outlined"
                    required
                />
            </>
        )
    }

    if (isLoadingData)
        return <ContentSkeleton />

    return (
        <>
            <Card>
                <CardHeader title='Edit feature plan'>
                </CardHeader>
                <CardBody>
                    <div className="d-flex align-item-center">
                        <div className="col-12">
                            <MultilanguageTabBlock
                                multilanguageTabContent={renderMultilanguageTabContent}
                            />
                        </div>
                    </div>

                    <div className="d-flex align-item-center mt-5">
                        <div className="col-3">
                            <Typography variant="h6" color="text.secondary">
                                Features
                            </Typography>
                        </div>
                        <div className="col-8" />
                        <div className="d-flex justify-content-end col-1">
                            <button
                                type="button"
                                className="btn btn-primary text-white"
                                onClick={() => {
                                    setFeatureSelected(getEmptyFeatures())
                                    setFeaturesOpen(true)
                                    setNewFeature(true)
                                }}
                            >
                                Add new
                            </button>
                        </div>
                    </div>
                    <TablePlan
                        data={featuresData}
                        setData={setFeatureData}
                        setFeatureSelected={setFeatureSelected}
                        setFeaturesOpen={setFeaturesOpen}
                        setNewFeature={setNewFeature}
                        setSelectedPlans={setSelectedPlans}
                        tableKey={tableKey}
                    />

                </CardBody>
            </Card>
            <Button
                onClick={() => history.push('/features')}
                variant="outlined"
                style={{ marginRight: '20px' }}>
                Back
            </Button>
            <Button
                onClick={() => saveFeature()}
                variant="outlined"
                color="primary"
                style={{ marginRight: '20px' }}>
                Save feature
            </Button>

            {featureDialog(featuresOpen, setFeaturesOpen, featureSelected, setFeatureSelected, plans, selectedPlans, setSelectedPlans, featuresData, setFeatureData, newFeature, setTableKey)}


            {featureId && (
                <>
                    <MuiThemeProvider theme={theme}>
                        <Button
                            onClick={() => setOpenConfirmDialog(true)}
                            variant="outlined"
                            color="secondary"
                            style={{ marginRight: "20px" }}
                        >
                            Delete feature
                        </Button>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                marginLeft: "auto",
                            }}
                        ></div>
                    </MuiThemeProvider>

                    <ConfirmDialog
                        title={"Are you sure you want to delete this feature?"}
                        open={openConfirmDialog}
                        setOpen={setOpenConfirmDialog}
                        onConfirm={() => {
                            deleteFeature(featureId)
                                .then((res) => {
                                    alertSuccess({
                                        title: "Deleted!",
                                        customMessage: "Feature deleted successfully.",
                                    });
                                    history.push("/features");
                                })
                                .catch((error) => {
                                    alertError({
                                        error: error,
                                        customMessage: "Could not delete the feature.",
                                    });
                                });
                        }}
                    />
                </>
            )}
        </>
    );
}